import { CheckCircle, X } from "lucide-react";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

export type ToastKind = "success" | "error";

export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  /** Set while the exit animation plays. */
  leaving?: boolean;
}

export interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
}

/**
 * Default is a no-op so components using useToast() still render outside a
 * provider (e.g. isolated unit tests); toasts simply don't appear.
 */
const ToastContext = createContext<ToastApi>({
  success: () => {},
  error: () => {},
});

export function useToast(): ToastApi {
  return useContext(ToastContext);
}

const EXIT_MS = 200;

export interface ToastProviderProps {
  /** How long success toasts stay before auto-dismissing. */
  timeoutMs?: number;
  children: React.ReactNode;
}

/**
 * Global popup toast stack (top-right, top-center on small screens).
 * Success toasts auto-dismiss; error toasts persist until dismissed.
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({
  timeoutMs = 2500,
  children,
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef<Map<number, number>>(new Map());
  // Render-synced mirror so push() can check for duplicates without
  // side effects inside a state updater.
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  const remove = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    // Flag it as leaving so the exit transition plays, then drop it.
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, leaving: true } : t))
    );
    window.setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      EXIT_MS
    );
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      // Don't stack an identical toast on top of a visible one (e.g. the
      // same validation error fired repeatedly).
      const duplicate = toastsRef.current.some(
        (t) => !t.leaving && t.kind === kind && t.message === message
      );
      if (duplicate) return;
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, kind, message }]);
      if (kind === "success") {
        timers.current.set(
          id,
          window.setTimeout(() => remove(id), timeoutMs)
        );
      }
    },
    [remove, timeoutMs]
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message: string) => push("success", message),
      error: (message: string) => push("error", message),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 top-4 z-[100] flex flex-col items-center gap-2 px-4 sm:inset-x-auto sm:right-4 sm:items-end"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.kind === "error" ? "alert" : "status"}
            className={`pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-lg border px-4 py-3 shadow-lg backdrop-blur transition-all duration-200 ${
              toast.leaving
                ? "-translate-y-2 opacity-0"
                : "translate-y-0 opacity-100"
            } ${
              toast.kind === "success"
                ? "border-green-500/40 bg-green-950/90 text-green-200"
                : "border-red-500/40 bg-red-950/90 text-red-200"
            }`}
          >
            {toast.kind === "success" ? (
              <CheckCircle className="h-5 w-5 shrink-0 text-green-400" />
            ) : (
              <X className="h-5 w-5 shrink-0 text-red-400" />
            )}
            <span className="flex-1 text-sm font-medium">{toast.message}</span>
            <button
              onClick={() => remove(toast.id)}
              aria-label="Dismiss"
              className={`rounded-full p-1 transition-colors hover:text-white ${
                toast.kind === "success"
                  ? "text-green-300 hover:bg-green-500/20"
                  : "text-red-300 hover:bg-red-500/20"
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
